import { useEffect, useMemo, useState } from "react";
import { STAMPS_PER_CARD, type Stamp } from "@pagemates/shared";
import { account } from "@pagemates/shared/copy";

const txt = account.libraryCard;

/*
 * The library card (PLAN.md §Profiles as library cards). A physical object,
 * so it's drawn in the material tokens and looks the same in both modes.
 * Rows are stamps, oldest first in the order they reached the card; twelve
 * to a card, then a fresh card and the full one goes into the pocket.
 * Screen readers get a plain list instead of the grid.
 */

/** Seeded by the stamp's id, so a stamp sits the same way on every visit. */
function hash(s: string) {
  let x = 2166136261;
  for (let i = 0; i < s.length; i++) { x ^= s.charCodeAt(i); x = Math.imul(x, 16777619); }
  return x >>> 0;
}
function ink(id: string): React.CSSProperties {
  const k = hash(id);
  const rot = ((k % 50) / 10 - 2.5).toFixed(2);
  const dx = ((k >> 4) % 5) - 2;
  return { transform: `translateX(${dx}px) rotate(${rot}deg)`, opacity: (0.74 + ((k >> 8) % 22) / 100).toFixed(2) };
}

const MONTHS = [txt.jan, txt.feb, txt.mar, txt.apr, txt.may, txt.jun, txt.jul, txt.aug, txt.sep, txt.oct, txt.nov, txt.dec];
export const stampDate = (s: Stamp) => {
  const d = new Date(s.at);
  return s.kind === "back" ? String(d.getFullYear()) : `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
export const longDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
export const monthYear = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" });

/** "Slaughterhouse-Five, finished 12 November 2026, 4 stars." */
export function spokenStamp(s: Stamp) {
  const stars = s.halfStars ? `, ${s.halfStars / 2} ${s.halfStars === 2 ? txt.star : txt.stars}` : "";
  if (s.kind === "dnf") return txt.returnedUnfinished(s.book.title, longDate(s.at));
  if (s.kind === "back") return txt.readIn(s.book.title, new Date(s.at).getFullYear(), stars);
  return txt.finished(s.book.title, longDate(s.at), stars);
}

function CardStars({ half }: { half: number }) {
  return (
    <span className="lc-stars" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => <span key={i} className="st" style={{ "--fill": `${Math.max(0, Math.min(1, half / 2 - (i - 1))) * 100}%` } as React.CSSProperties} />)}
    </span>
  );
}

export function chunkCards(stamps: Stamp[]): Stamp[][] {
  const cards: Stamp[][] = [];
  for (let i = 0; i < stamps.length; i += STAMPS_PER_CARD) cards.push(stamps.slice(i, i + STAMPS_PER_CARD));
  return cards.length ? cards : [[]];
}

/** One card. `no` is typed in the corner; `clubLine` says whose stamps these are ("Club A", "3 clubs"). */
export function Card({ name, no, clubLine, issuedAt, stamps, onStamp, mini, className, rows = STAMPS_PER_CARD }:
  { name: string; no: number; clubLine?: string | null; issuedAt?: string | null; stamps: Stamp[]; onStamp?: (s: Stamp) => void; mini?: boolean; className?: string; rows?: number }) {
  const blanks = Math.max(0, rows - stamps.length);
  return (
    <div className={`lc${mini ? " mini" : ""}${className ? ` ${className}` : ""}`}>
      <div className="lc-hd" aria-hidden="true"><span>{txt.pagemates}</span><span className="no">{txt.no(no)}</span></div>
      <div className="lc-name"><small aria-hidden="true">{txt.reader}</small><b>{name}</b></div>
      <div className="lc-issued" aria-hidden="true"><span>{clubLine ?? ""}</span>{issuedAt && <span>{txt.issued(monthYear(issuedAt).toUpperCase())}</span>}</div>
      {!mini && (
        <ol className="sr-only" aria-label={txt.sLibraryCardNo(name, no, stamps.length, stamps.length === 1 ? txt.book : txt.books)}>
          {stamps.map((s) => <li key={s.id}>{onStamp ? <button type="button" onClick={() => onStamp(s)}>{spokenStamp(s)}</button> : spokenStamp(s)}</li>)}
        </ol>
      )}
      <div className="grid" aria-hidden="true">
        <div className="gh"><span className="c">{txt.date}</span><span>{txt.title}</span></div>
        {stamps.map((s) => (
          <div key={s.id} className={`r ${s.kind}${onStamp ? " tap" : ""}`} onClick={onStamp ? () => onStamp(s) : undefined}>
            <span className="dt" style={s.kind === "back" ? undefined : ink(s.id)}>{stampDate(s)}{s.kind === "dnf" && <em>{txt.returned}</em>}</span>
            <span className="ti">{s.book.title}</span>
            {!mini && s.kind !== "dnf" && s.halfStars ? <CardStars half={s.halfStars} /> : <span />}
          </div>
        ))}
        {Array.from({ length: blanks }, (_, i) => <div key={`b${i}`} className="r" />)}
      </div>
    </div>
  );
}

/**
 * The card on top and the pocket of full ones under it. Tapping the pocket
 * fans the earlier cards out; tapping one shows it full size. On your own
 * card, `fresh` plays the "new card issued" moment once.
 */
export function CardStack({ name, issuedAt, clubLine, stamps, onStamp, fresh, onFreshDone }:
  { name: string; issuedAt: string | null; clubLine: string | null; stamps: Stamp[]; onStamp?: (s: Stamp) => void; fresh?: boolean; onFreshDone?: () => void }) {
  const cards = useMemo(() => chunkCards(stamps), [stamps]);
  const last = cards.length - 1;
  const [viewing, setViewing] = useState(last);
  const [fan, setFan] = useState(false);
  const [moment, setMoment] = useState(Boolean(fresh && last > 0));
  const [note] = useState(Boolean(fresh && last > 0));
  useEffect(() => { setViewing(last); }, [last]);
  useEffect(() => {
    if (!fresh) return;
    onFreshDone?.(); // shown once: remembered as soon as it starts
    const t = window.setTimeout(() => setMoment(false), 2400);
    return () => window.clearTimeout(t);
  }, [fresh]); // eslint-disable-line react-hooks/exhaustive-deps
  const earlier = last; // cards in the pocket
  const card = (i: number, extra?: string) => <Card key={i} name={name} no={i + 1} clubLine={clubLine} issuedAt={issuedAt} stamps={cards[i]!} onStamp={onStamp} className={extra} />;

  if (moment) {
    return (
      <div className="lc-stack" aria-live="polite">
        <p className="sr-only">{txt.cardNoIsFull(last, last + 1)}</p>
        <div className="lc-renew">
          {card(last - 1, "old")}
          {card(last, "fresh")}
        </div>
        <Pocket count={earlier} />
      </div>
    );
  }
  return (
    <div className="lc-stack">
      {viewing !== last && <button type="button" className="linkish small lc-back" onClick={() => setViewing(last)}>{txt.backToCardNo(last + 1)}</button>}
      {card(viewing)}
      {note && viewing === last && <p className="lc-note">{txt.cardNoIsFull(last, last + 1)}</p>}
      {earlier > 0 && <Pocket count={earlier} open={fan} onClick={() => setFan((f) => !f)} />}
      {fan && (
        <div className="lc-fan" role="list" aria-label={txt.earlierCards}>
          {cards.slice(0, last).map((st, i) => (
            <button key={i} type="button" role="listitem" className="lc-fan-item" aria-label={txt.cardNo(i + 1)} onClick={() => { setViewing(i); setFan(false); }}>
              <Card name={name} no={i + 1} stamps={st} mini />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Pocket({ count, open, onClick }: { count: number; open?: boolean; onClick?: () => void }) {
  const label = txt.earlier(count, count === 1 ? txt.card : txt.cards);
  const body = <><span className="peek" />{count > 1 && <span className="peek p2" />}<span className="body">{label}</span></>;
  return onClick
    ? <button type="button" className="pocket" aria-expanded={open} aria-label={open ? txt.hide(label) : txt.show(label)} onClick={onClick}>{body}</button>
    : <div className="pocket" aria-hidden="true">{body}</div>;
}
